/**
 * Cola de uploads resiliente con persistencia IndexedDB
 *
 * Archivos sobreviven page refresh via IndexedDB.
 * Compone sobre createBaseQueue para reutilizar lógica de retry/backoff.
 *
 * @module services/uploadQueue
 * @example
 * import uploadQueue from './services/uploadQueue';
 * uploadQueue.add({ file, uploadFn, metadata: { actividadId: 1, tipo: 'antes' } });
 */

import { createBaseQueue } from './baseQueue';
import offlineDb from './offlineDb';

function createUploadQueue() {
  return createBaseQueue({
    maxRetries: 3,
    backoffBaseMs: 2000,

    async processItem(item) {
      let file = item.file;
      // Si no hay file en memoria, intentar restaurar desde IndexedDB
      if (!file && item.fileStoreId) {
        const stored = await offlineDb.getFile(item.fileStoreId);
        if (stored && stored.file) {
          file = stored.file;
        }
      }
      if (!file || !item.uploadFn) {
        throw new Error('Archivo perdido. Seleccione nuevamente.');
      }
      await item.uploadFn(file, item.metadata);
    },

    async persist(queue) {
      try {
        await offlineDb.saveUploadQueue(queue);
      } catch { /* ignore */ }
    },

    async restore() {
      try {
        let items = await offlineDb.getUploadQueue();
        // Migración one-time desde localStorage
        if (!items || items.length === 0) {
          const stored = localStorage.getItem('uploadQueue_metadata');
          if (!stored) return [];
          items = JSON.parse(stored);
          await offlineDb.saveUploadQueue(items);
          localStorage.removeItem('uploadQueue_metadata');
        }
        return items
          .map(item => ({
            ...item,
            file: null,
            uploadFn: null,
            status: item.fileStoreId ? 'pending' : 'orphan'
          }))
          .filter(item => item.fileStoreId);  // Descartar orphans irrecuperables
      } catch {
        try { localStorage.removeItem('uploadQueue_metadata'); } catch {}
        return [];
      }
    }
  });
}

const uploadQueue = createUploadQueue();

// Wrap add to store file in IndexedDB before enqueueing
const originalAdd = uploadQueue.add;
uploadQueue.add = async function({ file, uploadFn, metadata = {} }) {
  let fileStoreId = null;
  try {
    fileStoreId = await offlineDb.storeFile({
      file,
      metadata: { fileName: file.name, fileSize: file.size }
    });
  } catch {
    // IndexedDB failed — still enqueue with in-memory file
  }
  return originalAdd.call(this, {
    file,
    uploadFn,
    metadata: { ...metadata, fileName: file.name, fileSize: file.size },
    fileStoreId
  });
};

export default uploadQueue;
